import { acceptHMRUpdate, defineStore } from "pinia";
import { computed, ref } from "vue";



import { Factory } from "@/data/dao/Factory";
import { CustomerDao } from "@/data/dao/service/CustomerDao";
import type { ICustomerArticle, ICustomerArticleWithOptions, ICustomerCategory, ICustomerOffer, ICustomerOptionGroup } from "@/data/model/Customer";
import type { IService } from "@/data/model/Service";



import { clientLanguage } from "@/shared/localization/language";
import { logError } from "@/shared/logging/errors";



import { canBeAddedWithPreselectedOptions } from "@/components/order/optionGroupsValidator";



import { globalStores } from "@/app/globalStores";
import { Helper } from "@/app/ui/Helper";



import { useArticleAttributesStore } from "./articleAttribute";
import { useCartStore } from "./cart";


export const useOfferStore = defineStore("offer", () => {
    const customerDao = Factory.getInstance<CustomerDao>(CustomerDao);

    const cart = useCartStore();
    const iconAttributes = useArticleAttributesStore();

    // define state
    const orderService = ref<IService | null>(null);
    const offer = ref<ICustomerOffer | null>(null);
    const activeCategory = ref<ICustomerCategory | null>(null);
    const isLoading = ref<boolean>(false);

    // define getters
    const itemCategories = computed(() => {
        return offer.value?.entries ?? [];
    });

    const highlightCategories = computed(() => {
        return itemCategories.value.filter(c => c.highlight && c.highlightType != null);
    });

    const hasItemCategories = computed(() => itemCategories.value.length > 0);
    const offerItems = computed(() => getArticlesWithOptions(activeCategory.value?.articles ?? []));
    const isPreorderService = computed(() => orderService.value?.useCase?.usePreorder ?? false);
    // gates everything discount related on the order screen: the % button and the cart item selection
    // that feeds it
    const areDiscountsEnabled = computed(() => orderService.value?.useCase?.useVouchersAndDiscounts ?? false);

    // define actions
    async function setOrderService(service: IService | null, reloadOffer: boolean = true) {
        orderService.value = service;

        if (orderService.value == null) {
            setCurrentOffer(null);
            return;
        }

        if (reloadOffer) {
            await loadOffer();
        }
    }

    async function loadOffer() {
        isLoading.value = true;
        try {
            const serviceId = orderService.value!.id;
            const useCase = orderService.value!.useCase;

            const offer = await customerDao.getOffer({
                serviceId: serviceId,
                useCaseId: useCase?.id,
                useCartOffer: isPreorderService.value, // should load offer assigned to cart if it's preorder
            });
            setCurrentOffer(offer);
        } finally {
            isLoading.value = false;
        }
    }

    function setCurrentOffer(currentOffer: ICustomerOffer | null) {
        offer.value =
            currentOffer != null && globalStores.kioskStore?.isKioskMode === true
                ? withoutPricePerMinuteArticles(currentOffer)
                : currentOffer;
        cart.setCurrentCart(currentOffer?.cart ?? null);

        if (currentOffer != null) {
            iconAttributes.load(currentOffer.locationId, clientLanguage()).catch(logError);
        }

        const categories = itemCategories.value;
        setActiveCategory(categories.length ? categories[0] : null);
    }

    // a kiosk pays immediately and a price per minute item can only be ordered with pay later, so the kiosk does not show
    // them, nor a category that is left without items
    function withoutPricePerMinuteArticles(currentOffer: ICustomerOffer): ICustomerOffer {
        return {
            ...currentOffer,
            entries: currentOffer.entries
                .map((category) => ({
                    ...category,
                    articles: category.articles.filter((article) => !article.isPricePerMinute),
                }))
                .filter((category) => category.articles.length > 0),
        };
    }

    function setActiveCategory(category: ICustomerCategory | null) {
        activeCategory.value = category;
    }

    function setIsLoading(newIsLoading: boolean) {
        isLoading.value = newIsLoading;
    }

    function getArticlesWithOptions(articles: ICustomerArticle[], offerToUse?: ICustomerOffer) {
        const articlesWithOptions: ICustomerArticleWithOptions[] = [];
        for (const article of articles) {
            const articleWithOptions: ICustomerArticleWithOptions = {
                ...article,
                options: getOptionsForArticle(article, offerToUse),
                canBeAddedWithoutUserSelection: false,
                hasNegativePrice: Helper.sitePrice(article) < 0 || (article.isOpenPriceEnabled && article.isNegativeOpenPrice)
            };
            articleWithOptions.canBeAddedWithoutUserSelection = canBeAddedWithPreselectedOptions(articleWithOptions);

            articlesWithOptions.push(articleWithOptions);
        }
        return articlesWithOptions;
    }

    function getOptionsForArticle(article: ICustomerArticle, offerToUse?: ICustomerOffer) {
        const currentOffer = offerToUse ?? offer.value;
        const offerOptionGroups = currentOffer?.offerOptionGroups ?? [];
        const options: ICustomerOptionGroup[] = [];

        for (const optionGroupId of article.sortedOptionGroups ?? []) {
            const optionGroup = offerOptionGroups.find((optionGroup) => optionGroup.optionGroupId === optionGroupId);
            if (optionGroup == null) {
                logError(`Option group ${optionGroupId} not found for article ${article.articleId}`);
                continue;
            }

            options.push(optionGroup);
        }
        return options;
    }

    return {
        // state
        orderService,
        offer,
        activeCategory,
        isLoading,

        // getters
        itemCategories,
        highlightCategories,
        hasItemCategories,
        offerItems,
        isPreorderService,
        areDiscountsEnabled,

        // actions
        setOrderService,
        setActiveCategory,
        loadOffer,
        setCurrentOffer,
        setIsLoading,
        getArticlesWithOptions,
    };
});

export type OfferStore = ReturnType<typeof useOfferStore>;

if (import.meta.hot) {
    import.meta.hot.accept(acceptHMRUpdate(useOfferStore, import.meta.hot));
}
